/**
 * Compute per-serving variant nutrition from ingredient quantities and
 * USDA per-100g data.
 *
 * Strategy:
 *   1. For each ingredient, convert the used quantity → grams (display pair
 *      preferred; shopping pair as fallback) and scale by consumedFraction —
 *      nutrition counts what the eater ingests, not what's on the shopping list
 *   2. Apply per-100g nutrient values to get total nutrient contribution
 *   3. Sum across all ingredients and divide by serving count
 *   4. Report coverage (fraction of ingredients that had usable USDA data)
 *
 * Returns null when zero ingredients have USDA data — caller falls back to
 * AI-estimated nutrition in that case.
 *
 * Coverage threshold for switching sources is enforced by the caller, not here,
 * so the caller can log the coverage value and make the decision transparently.
 */

// Unit → grams (mass units only — no density needed)
const MASS_UNIT_TO_GRAMS: Record<string, number> = {
  g: 1,
  gram: 1,
  grams: 1,
  kg: 1000,
  kilogram: 1000,
  kilograms: 1000,
  mg: 0.001,
  milligram: 0.001,
  oz: 28.3495,
  ounce: 28.3495,
  ounces: 28.3495,
  lb: 453.592,
  lbs: 453.592,
  pound: 453.592,
  pounds: 453.592,
};

// Unit → millilitres (volume units — needs densityGPerMl to reach grams)
const VOLUME_UNIT_TO_ML: Record<string, number> = {
  ml: 1,
  milliliter: 1,
  millilitre: 1,
  milliliters: 1,
  millilitres: 1,
  l: 1000,
  liter: 1000,
  litre: 1000,
  liters: 1000,
  litres: 1000,
  dl: 100,
  deciliter: 100,
  tsp: 4.929,
  teaspoon: 4.929,
  teaspoons: 4.929,
  tbsp: 14.787,
  tablespoon: 14.787,
  tablespoons: 14.787,
  cup: 236.588,
  cups: 236.588,
  'fl oz': 29.574,
  'fluid oz': 29.574,
  'fluid ounce': 29.574,
  'fluid ounces': 29.574,
  pt: 473.176,
  pint: 473.176,
  pints: 473.176,
  qt: 946.353,
  quart: 946.353,
  quarts: 946.353,
};

// Unit keywords that indicate a countable piece (needs gPerPiece to reach grams)
const COUNT_UNIT_KEYWORDS = new Set([
  'pc',
  'piece',
  'pieces',
  'whole',
  'each',
  'clove',
  'cloves',
  'head',
  'heads',
  'bunch',
  'bunches',
  'slice',
  'slices',
  'stalk',
  'stalks',
  'sprig',
  'sprigs',
  'leaf',
  'leaves',
  'strip',
  'strips',
  'fillet',
  'fillets',
  'pinch',
]);

// Last-resort gram defaults for count units whose ingredient lacks gPerPiece.
// These are seasoning-scale units where exact weight barely matters for
// macros (a "pinch" is half a gram of anything — calories rounding error).
// Used only when gPerPiece is null: better to include the ingredient with a
// trace estimate than drop it and pull coverage under 50%.
//
// Deliberately omits 'pc' / 'piece' / 'whole' / 'each' — those describe whole
// foods (1 lemon, 1 chicken breast) where a default would silently mis-weight
// the recipe by orders of magnitude.
const COUNT_FALLBACK_GRAMS: Record<string, number> = {
  pinch: 0.5,
  bunch: 20,
  bunches: 20,
  sprig: 1,
  sprigs: 1,
  leaf: 0.3,
  leaves: 0.3,
};

export interface IngredientForNutrition {
  /**
   * The ingredient's name, carried only so a caller that REJECTS the result can
   * say which ingredient blocked it (#808). Never used in the arithmetic.
   * Optional — callers that only want the totals need not supply it.
   */
  name?: string | null;
  caloriesPer100g: number | null;
  proteinGPer100g: number | null;
  carbsGPer100g: number | null;
  fatGPer100g: number | null;
  fiberGPer100g: number | null;
  densityGPerMl: number | null;
  gPerPiece: number | null;
  shoppingQuantity: number | null;
  shoppingUnit: string | null;
  /**
   * The amount the recipe actually uses. Preferred over shoppingQuantity,
   * which carries purchase rounding (display 0.5 pc lemon → shopping 1 pc
   * would double the lemon's calories). Optional for backwards compatibility —
   * when absent, the shopping pair is used as before.
   */
  displayQuantity?: number | null;
  displayUnit?: string | null;
  /**
   * Fraction (0–1) of the ingredient the eater actually ingests — frying or
   * confit oil left behind, drained marinades, salted pasta water. Null /
   * absent = 1 (fully consumed).
   */
  consumedFraction?: number | null;
}

export interface ComputedNutrition {
  calories: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  fiberG: number;
  /** Per-serving ingested weight in grams (consumed fractions applied). Used for calorie density. */
  totalWeightG: number;
  /** 0–1: fraction of ingredients that contributed USDA data to this result */
  coverage: number;
  /**
   * Count of ingredients that HAD usable nutrition data and a quantity+unit but
   * could NOT be converted to grams (e.g. a count unit with no g_per_piece), so
   * their calories were silently dropped from the totals. Unlike low coverage
   * (an ingredient with no nutrition data at all — acceptable), a non-zero value
   * here means the computed total is KNOWN to be missing real food. Callers
   * should not present such a total as authoritative — prefer the AI estimate.
   */
  unconvertedWithData: number;
  /**
   * Names of the ingredients counted in `unconvertedWithData`, in list order —
   * empty when that count is zero, and populated only for ingredients whose
   * caller supplied a `name`.
   *
   * A count alone tells you a recompute was refused but not what to do about
   * it, which is how a single unweighable ingredient can park a recipe on stale
   * nutrition indefinitely with nothing naming the culprit (#808). Two real
   * cases: a lasagne's "rosemary, quantity 2, no unit at all", and an arayes
   * measuring a whole-lemon food in millilitres. Both were one field away from
   * computing, and neither was discoverable from the run output.
   */
  unconvertedNames: string[];
  /**
   * Worst-case per-serving calories the `unconvertedWithData` ingredients could
   * have added, or `null` when even one of them is UNBOUNDED (#867).
   *
   * `null` rather than `Infinity` on purpose. `JSON.stringify(Infinity)` is
   * `null`, so the moment this value crossed a Trigger payload or a log
   * boundary it would arrive as null anyway — and `null > threshold` is
   * `false`, which would wave through exactly the unbounded case this exists to
   * catch. Making the absent case a real null forces every consumer to handle
   * it, and TypeScript enforces that at the call site instead of leaving it to
   * whoever remembers.
   *
   * `unconvertedWithData` answers "did we drop real food?". It cannot answer
   * "does it matter?", and treating those as the same question is what made one
   * missing density discard nineteen correctly-weighed ingredients in favour of
   * the model's whole-dish guess. A spoon of harissa on a sheet-pan dinner
   * cannot move the total by more than ~1%; a `pc` of an unknown food could be
   * anything. Callers gate on the MAGNITUDE, not on the count.
   *
   * When non-null, always ≥ 0 — and 0 exactly when `unconvertedWithData` is 0.
   */
  unconvertedMaxCalories: number | null;
}

/**
 * Densest thing a home cook measures by spoon or jug. Honey and golden syrup
 * sit at ~1.42 g/ml, fine salt ~1.2, so 1.5 is above every real food while
 * staying a real number rather than a shrug.
 *
 * Used ONLY to put a CEILING on an ingredient we failed to weigh (#867). It
 * never feeds a total: assuming a density in the arithmetic is exactly what
 * `toGrams` refuses to do, because a wrong conversion is worse than none. An
 * upper bound is a different claim — "this omission cannot be larger than X" —
 * and unlike a guess it can only ever be pessimistic.
 */
const MAX_FOOD_DENSITY_G_PER_ML = 1.5;

/**
 * The most an unweighable ingredient could possibly contribute, in grams, or
 * `Infinity` when nothing bounds it.
 *
 * A VOLUME we know: "3 tbsp" is 45 ml whatever the food is, so its mass cannot
 * exceed 45 × the densest food there is. A COUNT we do not: "1 pc" of an
 * unknown food could be a bay leaf or a whole squash, and no constant separates
 * those. That asymmetry is the whole reason this returns Infinity rather than
 * a large number — a bound you invented is not a bound.
 */
function maxPossibleGrams(quantity: number, unit: string | null): number {
  if (!unit) return Infinity;
  const u = unit.trim().toLowerCase();

  const massFactor = MASS_UNIT_TO_GRAMS[u];
  if (massFactor != null) return quantity * massFactor;

  const volumeFactor = VOLUME_UNIT_TO_ML[u];
  if (volumeFactor != null) return quantity * volumeFactor * MAX_FOOD_DENSITY_G_PER_ML;

  return Infinity;
}

/**
 * Convert an ingredient quantity to grams, or return null if conversion
 * is not possible (unknown unit, missing density/gPerPiece).
 */
export function toGrams(
  quantity: number,
  unit: string | null,
  densityGPerMl: number | null,
  gPerPiece: number | null,
): number | null {
  if (!unit) {
    if (gPerPiece == null) return null;
    return quantity * gPerPiece;
  }
  const u = unit.trim().toLowerCase();

  const massFactor = MASS_UNIT_TO_GRAMS[u];
  if (massFactor != null) return quantity * massFactor;

  const volumeFactor = VOLUME_UNIT_TO_ML[u];
  if (volumeFactor != null) {
    // Volume → grams REQUIRES the food's density. Assuming water (1.0 g/ml) when
    // no density row exists silently mis-weighs anything that isn't water-like —
    // grated parmesan (~0.4 g/ml), spinach leaves (~0.1 g/ml) — by 2.6–8x, which
    // then pollutes a total we stamp "computed". A wrong conversion is worse than
    // none (density-table.ts), so return null: the caller counts it in
    // unconvertedWithData and the honesty gate downgrades the source.
    if (densityGPerMl == null) return null;
    return quantity * volumeFactor * densityGPerMl;
  }

  if (COUNT_UNIT_KEYWORDS.has(u) || u === '') {
    // Count → grams requires weight per piece. When that's missing, fall back
    // to a unit-level constant for seasoning-scale units only — see
    // COUNT_FALLBACK_GRAMS rationale.
    if (gPerPiece != null) return quantity * gPerPiece;
    const fallback = COUNT_FALLBACK_GRAMS[u];
    if (fallback != null) return quantity * fallback;
    return null;
  }

  return null;
}

/**
 * Resolve the grams of an ingredient the eater actually ingests, or null when
 * the quantity can't be converted. Single source of truth for the quantity →
 * grams policy, shared by the server compute path (computeVariantNutrition)
 * and the client nutrition-breakdown modal — the two MUST agree or the
 * breakdown totals drift from the stored per-serving values:
 *   - display pair preferred over the shopping pair (which carries purchase
 *     rounding — display 900 g spaghetti, shopping 1000 g)
 *   - an omitted unit on one pair IS the other pair's unit (mirrors the save
 *     path)
 *   - consumedFraction scales down what's discarded (frying oil, pasta water)
 */
export function resolveConsumedGrams(ing: {
  displayQuantity?: number | null;
  displayUnit?: string | null;
  shoppingQuantity: number | null;
  shoppingUnit: string | null;
  densityGPerMl: number | null;
  gPerPiece: number | null;
  consumedFraction?: number | null;
}): number | null {
  let grams: number | null = null;
  if (ing.displayQuantity != null) {
    grams = toGrams(ing.displayQuantity, ing.displayUnit ?? ing.shoppingUnit ?? null, ing.densityGPerMl, ing.gPerPiece);
  }
  if (grams === null && ing.shoppingQuantity != null) {
    grams = toGrams(ing.shoppingQuantity, ing.shoppingUnit ?? ing.displayUnit ?? null, ing.densityGPerMl, ing.gPerPiece);
  }
  if (grams === null) return null;

  const consumedFraction = Math.min(1, Math.max(0, ing.consumedFraction ?? 1));
  return grams * consumedFraction;
}

/**
 * Upper bound on the grams an ingredient could contribute, mirroring
 * `resolveConsumedGrams`'s pair selection exactly (#867).
 *
 * Same order (display pair first, an omitted unit inherits the other pair's),
 * same consumedFraction scaling — because a bound derived from a DIFFERENT
 * quantity than the one we failed to convert would not bound anything.
 */
export function maxPossibleConsumedGrams(ing: {
  displayQuantity?: number | null;
  displayUnit?: string | null;
  shoppingQuantity: number | null;
  shoppingUnit: string | null;
  consumedFraction?: number | null;
}): number {
  let grams = Infinity;
  if (ing.displayQuantity != null) {
    grams = maxPossibleGrams(ing.displayQuantity, ing.displayUnit ?? ing.shoppingUnit ?? null);
  }
  if (!Number.isFinite(grams) && ing.shoppingQuantity != null) {
    grams = maxPossibleGrams(ing.shoppingQuantity, ing.shoppingUnit ?? ing.displayUnit ?? null);
  }
  if (!Number.isFinite(grams)) return Infinity;

  const consumedFraction = Math.min(1, Math.max(0, ing.consumedFraction ?? 1));
  return grams * consumedFraction;
}

/**
 * Compute per-serving nutrition from ingredient USDA data.
 *
 * Returns null only when no ingredient had usable USDA data at all.
 * Partial coverage (some ingredients without data) is reflected in the
 * coverage field so callers can decide whether to trust the result.
 */
export function computeVariantNutrition(
  ingredients: IngredientForNutrition[],
  servings: number,
): ComputedNutrition | null {
  const effectiveServings = Math.max(1, servings);

  let totalCalories = 0;
  let totalProtein = 0;
  let totalCarbs = 0;
  let totalFat = 0;
  let totalFiber = 0;
  let totalGrams = 0;
  let coveredCount = 0;
  let unconvertedWithData = 0;
  let unconvertedMaxCalories = 0;
  const unconvertedNames: string[] = [];

  for (const ing of ingredients) {
    // Only skip when the ingredient truly can't contribute a weighed macro: no
    // nutrition data or no quantity at all ("to taste"). A NULL unit is
    // legitimate for count items (eggs) — toGrams resolves those via gPerPiece
    // — so it must NOT gate the ingredient out here; if it genuinely can't be
    // weighed, toGrams returns null below and it's counted as unconverted
    // (not silently dropped).
    if (ing.caloriesPer100g === null || (ing.displayQuantity == null && ing.shoppingQuantity == null)) {
      continue;
    }

    const consumedGrams = resolveConsumedGrams(ing);
    if (consumedGrams === null) {
      // Nutrition data exists but the quantity couldn't be weighed (e.g. a count
      // unit or NULL unit with no g_per_piece, or a volume unit with no density).
      // This silently drops real calories — record it so the caller can refuse
      // to trust the total, and name it so the caller can say what to fix.
      // See unconvertedWithData / unconvertedNames.
      unconvertedWithData++;
      if (ing.name) unconvertedNames.push(ing.name);
      // How much this omission could be worth, at most. A volume is boundable
      // whatever the food; a count of an unknown food is not, and that one
      // makes the whole bound Infinity — a total is only as trustworthy as its
      // least-bounded omission.
      unconvertedMaxCalories +=
        (ing.caloriesPer100g * maxPossibleConsumedGrams(ing)) / 100;
      continue;
    }

    const factor = consumedGrams / 100;
    totalCalories += ing.caloriesPer100g * factor;
    totalProtein += (ing.proteinGPer100g ?? 0) * factor;
    totalCarbs += (ing.carbsGPer100g ?? 0) * factor;
    totalFat += (ing.fatGPer100g ?? 0) * factor;
    totalFiber += (ing.fiberGPer100g ?? 0) * factor;
    totalGrams += consumedGrams;
    coveredCount++;
  }

  if (coveredCount === 0) return null;

  const round1 = (n: number) => Math.round(n * 10) / 10;

  return {
    calories: round1(totalCalories / effectiveServings),
    proteinG: round1(totalProtein / effectiveServings),
    carbsG: round1(totalCarbs / effectiveServings),
    fatG: round1(totalFat / effectiveServings),
    fiberG: round1(totalFiber / effectiveServings),
    totalWeightG: round1(totalGrams / effectiveServings),
    coverage: coveredCount / ingredients.length,
    unconvertedWithData,
    unconvertedNames,
    // Per serving, to match every other figure here — so a caller can compare
    // it against `calories` directly without re-deriving the divisor.
    unconvertedMaxCalories: Number.isFinite(unconvertedMaxCalories)
      ? round1(unconvertedMaxCalories / effectiveServings)
      : null,
  };
}

// ---------------------------------------------------------------------------
// Is a computed total fit to store? (#867, #1018)
// ---------------------------------------------------------------------------

/**
 * How much of a variant's per-serving calories the ingredients we could NOT
 * weigh are allowed to account for, at their theoretical maximum, before the
 * computed total is refused (#867).
 *
 * 3% is below the noise already inside a "computed" figure — USDA per-100g
 * values are population averages, and the same onion varies more than that
 * between two supermarkets. So an omission provably under this ceiling does not
 * change what the number means, while one above it might.
 *
 * This is a ceiling on the WORST CASE, not on the expected error: the real miss
 * is typically far smaller, because the bound assumes every unweighable
 * ingredient is as dense as honey.
 */
export const MAX_UNCONVERTED_CALORIE_SHARE = 0.03;

export type NutritionVerdictReason =
  | 'accepted'
  | 'accepted-bounded-omission'
  | 'no-result'
  | 'low-coverage'
  | 'unconverted-too-large'
  | 'implausible-calories';

export interface NutritionVerdict {
  /** True when the computed figure should be stored in place of the AI estimate. */
  accept: boolean;
  reason: NutritionVerdictReason;
  /**
   * The unweighable ingredients' worst-case share of the per-serving total, or
   * `Infinity` when even one of them is unbounded. Null when nothing was
   * dropped. Carried so callers can log WHY without recomputing it.
   */
  worstCaseShare: number | null;
  /** Names of the ingredients that could not be weighed, for the log line. */
  unconvertedNames: string[];
}

/**
 * The ONE decision about whether a computed total replaces the AI estimate.
 *
 * ## Why this is shared rather than inlined
 *
 * It was inlined, four times — `generate-recipe`, `derive-variant`,
 * `iterate-variant` and `recompute-variant-nutrition` each wrote their own
 * predicate. #867 then fixed the interesting half of it in ONE of them.
 *
 * That half: judge a dropped ingredient by its SIZE, not by its existence.
 * `unconvertedWithData` answers "did we drop real food?" and cannot answer
 * "does it matter?". `unconvertedMaxCalories` is a true ceiling — a known
 * volume cannot weigh more than that volume times the densest food there is —
 * so a trace volume is provably harmless, while a count unit of an unknown food
 * has no ceiling at all and must still block. The old presence test traded a
 * bounded ~1% error for the model's unbounded whole-dish guess, in the name of
 * accuracy.
 *
 * With the fix in one copy and the old rule in the other three, every path a
 * COOK can trigger kept the old behaviour and only the nightly sweep had the
 * new one: half a teaspoon of liquid smoke with no density discarded a
 * computation over 21 correctly-weighed ingredients and stored 640 AI-estimated
 * kcal, which the 03:30 sweep then quietly repaired — so the wrong number was
 * visible exactly in the hours after an edit, and gone by morning (#1018).
 *
 * Hence one function. A fifth writer gets the rule by calling it, not by
 * remembering it (CLAUDE.md §19.7).
 */
export function judgeComputedNutrition(result: ComputedNutrition | null): NutritionVerdict {
  if (result === null) {
    return { accept: false, reason: 'no-result', worstCaseShare: null, unconvertedNames: [] };
  }

  const unconvertedNames = result.unconvertedNames;

  if (result.coverage < 0.5) {
    return { accept: false, reason: 'low-coverage', worstCaseShare: null, unconvertedNames };
  }

  let worstCaseShare: number | null = null;
  if (result.unconvertedWithData > 0) {
    // null = unbounded (a count unit of an unknown food). Handled FIRST and
    // explicitly: treating it as a number would make it 0 and wave through the
    // one case that has no ceiling at all.
    worstCaseShare =
      result.unconvertedMaxCalories === null
        ? Infinity
        : result.unconvertedMaxCalories / Math.max(result.calories, 1);
    if (worstCaseShare > MAX_UNCONVERTED_CALORIE_SHARE) {
      return { accept: false, reason: 'unconverted-too-large', worstCaseShare, unconvertedNames };
    }
  }

  // Checked AFTER the omission test so an implausible total is reported as
  // implausible rather than as an omission that happened to sit beside one.
  if (result.calories < 50 || result.calories > 2500) {
    return { accept: false, reason: 'implausible-calories', worstCaseShare, unconvertedNames };
  }

  return {
    accept: true,
    reason: result.unconvertedWithData > 0 ? 'accepted-bounded-omission' : 'accepted',
    worstCaseShare,
    unconvertedNames,
  };
}
